import omit from 'lodash/omit';
import throttle from 'lodash/throttle';
import { useEffect, useRef, useState } from 'react';
import { FreeMode, Keyboard, Mousewheel } from 'swiper';
import type { SwiperRef } from 'swiper/react';
import { SwiperSlide } from 'swiper/react';

import type {
  ContentfulNavQuickLinkFragment,
  ContentfulNavQuickLinksFragment,
} from '@/graphql/contentful/api';
import { useHeaderOffSet } from '@/hooks/useHeaderOffSet';
import { useIsMdDown } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { useToggle } from '@/hooks/useToggle';
import { containsPattern } from '@/lib/util';

import { Visibility } from '../../Visibility';
import { HistoryNavigations } from '../HistoryNavigations/HistoryNavigations';
import { NavQuickLinkBannerItem } from './components/NavQuickLinkBannerItem';
import { NavQuickLinkExhibitionsItem } from './components/NavQuickLinkExhibitionsItem/NavQuickLinkExhibitionsItem';
import { NavQuickLinkItem } from './components/NavQuickLinkItem';
import { NavQuickLinkPillItem } from './components/NavQuickLinkPillItem/NavQuickLinkPillItem';
import { NavQuickLinkStoriesItem } from './components/NavQuickLinkStoriesItem/NavQuickLinkStoriesItem';
import { NavQuickLinksVertical } from './components/NavQuickLinksVertical/NavQuickLinksVertical';

import * as S from './NavQuickLinks.styles';

export interface AccordionProps extends ContentfulNavQuickLinkFragment {
  handleChange?: () => void;
  isOpen?: boolean;
  openType: boolean;
  setCurrentQuickLink?: (quickLink: ContentfulNavQuickLinkFragment) => void;
}
export interface NavQuickLinksPillsProps extends ContentfulNavQuickLinksFragment {
  asPath: string;
}

export const NavQuickLinks = ({
  isSticky,
  linksCollection,
  navQuickLinkBackgroundColor,
  sys,
  type,
}: ContentfulNavQuickLinksFragment) => {
  const top = useHeaderOffSet();
  const { asPath } = useRouter();

  switch (type) {
    case 'Banner':
      return (
        <Banner
          data-test-id='NavQuickLinksBanner'
          linksCollection={linksCollection}
          sys={{
            id: 'Banner',
          }}
        ></Banner>
      );
    case 'Exhibition':
      return (
        <Exhibitions
          data-test-id='NavQuickLinksExhibitions'
          linksCollection={linksCollection}
          navQuickLinkBackgroundColor={navQuickLinkBackgroundColor}
          sys={{
            id: 'Exhibition',
          }}
        ></Exhibitions>
      );
    case 'Stories':
      return (
        <Stories
          data-test-id='NavQuickLinksStories'
          linksCollection={linksCollection}
          navQuickLinkBackgroundColor={navQuickLinkBackgroundColor}
          sys={{
            id: 'Stories',
          }}
        ></Stories>
      );

    case 'Vertical Links':
      return (
        <VerticalLinks
          linksCollection={linksCollection}
          sys={{
            id: 'Vertical Links',
          }}
        ></VerticalLinks>
      );
    case 'HistoryBottomNavigation':
      return (
        <HistoryNavigations
          linksCollection={linksCollection}
          sys={{
            id: 'HistoryBottomNavigation',
          }}
        ></HistoryNavigations>
      );
    default:
      return (
        <S.InnerWrapper
          key={sys?.id}
          $navQuickLinkBackgroundColor={navQuickLinkBackgroundColor as string}
          $sticky={isSticky as boolean}
          $stickyTop={top}
          data-test-id='NavQuickLinksInnerWrapper'
        >
          <S.HeaderBox data-test-id='NavQuickLinksHeaderBox'>
            {type === 'Pill' && (
              <S.FlexBox data-test-id='NavQuickLinksFlex'>
                <Pill
                  asPath={asPath}
                  data-test-id='NavQuickLinksPill'
                  linksCollection={linksCollection}
                  sys={{
                    id: 'Pill',
                  }}
                ></Pill>
              </S.FlexBox>
            )}
            {type === 'Bubble' && (
              <S.FlexBox data-test-id='NavQuickLinksFlex'>
                <Bubble
                  data-test-id='NavQuickLinksBubble'
                  linksCollection={linksCollection}
                  sys={{
                    id: 'Bubble',
                  }}
                ></Bubble>
              </S.FlexBox>
            )}
            {type === 'Text' && (
              <S.TextFlexBox data-test-id='NavQuickLinksFlex'>
                <LinkButtonNav
                  data-test-id='NavQuickLinksButton'
                  linksCollection={linksCollection}
                  sys={{
                    id: 'LinkButtonNav',
                  }}
                  type={type}
                ></LinkButtonNav>
              </S.TextFlexBox>
            )}
          </S.HeaderBox>
        </S.InnerWrapper>
      );
  }
};
export const Pill = ({ asPath, linksCollection }: NavQuickLinksPillsProps) => {
  if (!linksCollection) return <div data-testid='emptyBox' />;

  const isLinkActive = (linkUrl?: string) => {
    if (!linkUrl || !asPath) return false;
    const urlRegex = new RegExp(linkUrl);
    return urlRegex.test(asPath);
  };
  return (
    <S.CategoryWrapper data-test-id='PillCategoryWrapper'>
      {linksCollection.items.map((item, index) => {
        const props = { ...item } as ContentfulNavQuickLinkFragment;
        return (
          <NavQuickLinkPillItem
            key={props?.sys?.id}
            data-test-id={`PillNavQuickLinkPillItem-${index}`}
            isCurrentLink={isLinkActive(props?.url ?? '')}
            {...props}
          />
        );
      })}
    </S.CategoryWrapper>
  );
};
export const Bubble = ({
  linksCollection,
}: ContentfulNavQuickLinksFragment) => {
  if (!linksCollection) return <div data-testid='emptyBox' />;
  return (
    <S.CategoryWrapper data-test-id='BubbleCategoryWrapper'>
      {linksCollection.items.map((item, index) => {
        const props = { ...item } as ContentfulNavQuickLinkFragment;
        return (
          <NavQuickLinkItem
            key={props?.sys?.id ?? index}
            data-test-id={`BubbleNavQuickLinkItem-${index}`}
            {...props}
          />
        );
      })}
    </S.CategoryWrapper>
  );
};
export const Banner = ({
  linksCollection,
}: ContentfulNavQuickLinksFragment) => {
  if (!linksCollection) return <div data-testid='emptyBox' />;
  return (
    <S.BannerCategoryWrapper data-test-id='BannerCategoryWrapper'>
      {linksCollection.items.map((item, index) => {
        const props = { ...item } as ContentfulNavQuickLinkFragment;
        return (
          <NavQuickLinkBannerItem
            key={props?.sys?.id}
            data-test-id={`BubbleNavQuickBannerItem-${index}`}
            {...props}
          />
        );
      })}
    </S.BannerCategoryWrapper>
  );
};

export const LinkButtonNav = ({
  linksCollection,
  type,
}: ContentfulNavQuickLinksFragment) => {
  const [isOpen, toggle] = useToggle();
  const intCurrent = linksCollection
    ?.items[0] as ContentfulNavQuickLinkFragment;
  const [currentQuickLink, setCurrentQuickLink] =
    useState<ContentfulNavQuickLinkFragment>(intCurrent);
  if (!linksCollection) return <div data-testid='emptyBox' />;
  return (
    <>
      <Visibility smVisible>
        <S.CategoryWrapper data-test-id='SquareCategoryWrapper'>
          {linksCollection?.items.map((item, index) => {
            const props = { ...item } as ContentfulNavQuickLinkFragment;
            return (
              <NavQuickLinkItem
                key={props?.sys?.id}
                data-testid={`SquareNavQuickLinkItem-${index}`}
                type={type}
                {...props}
              />
            );
          })}
        </S.CategoryWrapper>
      </Visibility>
      <Visibility smHidden>
        <S.AccordionWrapper
          isOpen={isOpen}
          onClick={toggle}
          title={
            <AccordionItem
              key='AccordionItem'
              {...currentQuickLink}
              isOpen={isOpen}
              openType={false}
            ></AccordionItem>
          }
        >
          {linksCollection?.items?.map((item) => {
            return (
              <AccordionItem
                key={item?.sys?.id}
                handleChange={toggle}
                openType={true}
                setCurrentQuickLink={setCurrentQuickLink}
                {...(item as ContentfulNavQuickLinkFragment)}
              ></AccordionItem>
            );
          })}
        </S.AccordionWrapper>
      </Visibility>
    </>
  );
};

export const AccordionItem = (props: AccordionProps) => {
  const quicklink = omit(props, 'setCurrentQuickLink');
  const isSvgImage = props?.media?.contentType?.includes('svg') ?? false;
  const handleChange = (quickLink: ContentfulNavQuickLinkFragment) => {
    if (!props.openType) {
      return props?.handleChange?.();
    }
    props?.setCurrentQuickLink?.(quickLink);
    if (props.openType)
      window.open(quickLink?.url ?? '', quickLink?.urlTarget ?? '_self');
  };
  return (
    <S.AccordionItem
      $isOpen={props?.isOpen}
      $isSvgImage={isSvgImage}
      className='AccordionItemStyle'
      data-testid='AccordionItem'
      onClick={() => handleChange(quicklink)}
    >
      {props?.media && (
        <S.AccordionBox>
          <S.Img
            alt={props?.media.title ?? ''}
            aria-hidden
            data-test-id='AccordionImage'
            height={24}
            loaderHeight={24}
            sizes='50vw'
            src={props?.media.url ?? ''}
            width={24}
          />
        </S.AccordionBox>
      )}
      {props?.urlText}
    </S.AccordionItem>
  );
};

export const Stories = ({
  linksCollection,
  navQuickLinkBackgroundColor,
}: ContentfulNavQuickLinksFragment) => {
  const swiperRef = useRef<SwiperRef>(null);
  const { asPath } = useRouter();
  const isMobileView = useIsMdDown();
  const currentIndex = linksCollection?.items?.findIndex((item) => {
    const isContains = containsPattern(asPath, item?.url);
    const type = item?.url === asPath || isContains;
    return type;
  });
  const getRulerWidth = () => {
    // Get length from cms to calculate how much the current image needs to be extended
    const linksLength = linksCollection?.items?.length ?? 0;

    return isMobileView ? linksLength * 170 : linksLength * 244;
  };
  useEffect(() => {
    if (currentIndex && swiperRef) {
      swiperRef?.current?.swiper?.slideTo(currentIndex);
    }
  }, [currentIndex]);

  if (!linksCollection) return <div data-testid='emptyBox' />;
  return (
    <S.StoresWithImageWrapper
      $backgroundColor={navQuickLinkBackgroundColor as string}
    >
      <S.SwiperBox>
        <S.StoresWrapper
          ref={swiperRef}
          centerInsufficientSlides
          centeredSlidesBounds
          data-test-id='StoriesCategoryWrapper'
          freeMode={!isMobileView}
          modules={[FreeMode]}
          slidesPerView='auto'
          spaceBetween={16}
        >
          {linksCollection.items.map((item, index) => {
            const props = { ...item } as ContentfulNavQuickLinkFragment;
            return (
              <SwiperSlide key={props?.sys?.id}>
                {index === 0 && <S.RulerImage $width={getRulerWidth() ?? 0} />}
                <NavQuickLinkStoriesItem
                  data-test-id={`StoriesNavQuickLinkItem-${index}`}
                  {...props}
                  backgroundColor={navQuickLinkBackgroundColor}
                  isSwiperActive={index === currentIndex}
                />
              </SwiperSlide>
            );
          })}
        </S.StoresWrapper>
      </S.SwiperBox>
    </S.StoresWithImageWrapper>
  );
};

export const Exhibitions = ({
  linksCollection,
  navQuickLinkBackgroundColor,
}: ContentfulNavQuickLinksFragment) => {
  const swiperRef = useRef<SwiperRef>(null);
  const isMobileView = useIsMdDown();
  const { asPath } = useRouter();
  const currentIndex = linksCollection?.items?.findIndex((item) => {
    const isContains = containsPattern(asPath, item?.url);
    const type = item?.url === asPath || isContains;

    return type;
  });

  useEffect(() => {
    if (currentIndex && swiperRef) {
      swiperRef?.current?.swiper?.slideTo(currentIndex);
    }
  }, [currentIndex]);
  if (!linksCollection) return <div data-testid='emptyBox' />;

  return (
    <S.ExhibitionsWithImageWrapper
      $backgroundColor={navQuickLinkBackgroundColor as string}
    >
      <S.ExhibitionsWrapper
        ref={swiperRef}
        data-test-id='StoriesCategoryWrapper'
        freeMode
        modules={[Mousewheel, Keyboard, FreeMode]}
        mousewheel={true}
        slidesPerView='auto'
      >
        {linksCollection.items.map((item, index) => {
          const props = { ...item } as ContentfulNavQuickLinkFragment;
          return (
            <SwiperSlide key={props?.sys?.id}>
              {index === 0 && !isMobileView && <S.ExhibitionsRulerImage />}
              <NavQuickLinkExhibitionsItem
                data-test-id={`StoriesNavQuickLinkItem-${index}`}
                {...props}
                backgroundColor={navQuickLinkBackgroundColor}
                isSwiperActive={index === currentIndex}
              />
            </SwiperSlide>
          );
        })}
      </S.ExhibitionsWrapper>
    </S.ExhibitionsWithImageWrapper>
  );
};
export const VerticalLinks = ({
  linksCollection,
}: ContentfulNavQuickLinksFragment) => {
  const [isSticky, setIsSticky] = useState(false);
  const verticalLinksRef = useRef<HTMLInputElement | null>(null);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const footerRef = useRef<HTMLElement | null>(null);
  const [footerHeight, setFooterHeight] = useState(600);
  const mainRef = useRef<HTMLElement | null>(null);
  const [mainHeight, setMainHeight] = useState(0);

  // Handle the stickiness of the element when scrolling down
  const handleStickyElement = (elementTopToDocumentTop: number) => {
    const shouldStick = elementTopToDocumentTop <= 0;
    if (shouldStick !== isSticky) {
      setIsSticky(shouldStick);
    }
  };
  //Works properly when scrolling down. Stopped at calculated position when scrolling up
  const handleScroll = throttle(() => {
    const currentScrollTop = window.scrollY;

    const element = verticalLinksRef.current;

    if (!element) return;

    const elementTopToDocumentTop = element.getBoundingClientRect().top;

    handleStickyElement(elementTopToDocumentTop);

    setLastScrollTop(currentScrollTop);
  }, 50);

  useEffect(() => {
    const footer = document.querySelector('footer');
    const main = document.querySelector('main');
    if (footer) {
      footerRef.current = footer;
      setFooterHeight(footerRef.current.offsetHeight);
    }
    if (main) {
      mainRef.current = main;
      setMainHeight(mainRef.current.offsetHeight);
    }
  }, []);
  useEffect(() => {
    const onScroll = () => {
      handleScroll();
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isSticky, lastScrollTop, footerRef]);
  if (!linksCollection) return <div data-testid='emptyBox' />;
  return (
    <S.VerticalWrapper
      ref={verticalLinksRef}
      $footerHeight={footerHeight}
      $isSticky={isSticky}
      $mainHeight={mainHeight}
      data-testid='NavQuickLinksVertical'
    >
      <NavQuickLinksVertical
        linksCollection={linksCollection}
        sys={{
          id: 'Vertical',
        }}
      ></NavQuickLinksVertical>
    </S.VerticalWrapper>
  );
};
NavQuickLinks.displayName = 'NavQuickLinks';
